/**
 * 简体中文 — DRAFT TRANSLATION, PENDING SIGN-OFF.
 *
 * Scope: the corporate pages (Home, The Group, Our Businesses, Governance,
 * Sustainability, Newsroom, Contact) plus the site chrome. Investor Relations
 * filings, announcement titles and bodies are never translated — they are the
 * official record as released to SGXNet, and neither are the CEO's own
 * quotations, which stay in the language he published them in.
 *
 * Legal entity names, brand names, product designations, addresses and figures
 * are left exactly as published. Regulated English terms are kept in brackets
 * where the English is the term of art.
 *
 * Anything omitted here falls back to the English wording.
 */
import { HERITAGE_YEARS } from '../data/heritage';

const zh = {
  PRIMARY_NAV: [
    { label: '首页' },
    { label: '投资者关系' },
    { label: '集团' },
    { label: '我们的业务' },
    { label: '公司治理' },
    { label: '可持续发展' },
    { label: '新闻中心' },
    { label: '联络我们' },
  ],

  FOOTER_COLUMNS: [
    {
      heading: '公司',
      links: [{ label: '首页' }, { label: '集团' }, { label: '我们的业务' }, { label: '公司治理' }],
    },
    { heading: '我们的业务' },
    {
      heading: '投资者',
      links: [
        { label: '公告' },
        { label: '财务业绩' },
        { label: '年度报告' },
        { label: '股份资料' },
        { label: '电邮提醒' },
        { label: '投资者联络' },
      ],
    },
    {
      heading: '联系',
      links: [{ label: '联络我们' }, { label: '新闻中心' }],
    },
  ],

  LEGAL_LINKS: [
    { label: '使用条款' },
    { label: '隐私政策' },
    { label: '免责声明' },
    { label: 'Cookie 政策' },
    { label: '重要通知' },
  ],

  UI: {
    common: {
      home: '首页',
      ir: '投资者关系 →',
      search: '搜索',
      searchAria: '搜索本网站',
      language: '语言',
      languageNote:
        '公司页面提供三种语言版本。投资者关系文件保持英文原文，与在 SGXNet 上发布的内容一致。',
      draftLabel: '译文草稿',
      draftNotice: '本页为译文草稿，尚待集团核准。若两者有差异，概以英文版本为准。',
      englishOnlyLabel: '仅提供英文',
      englishOnlyNotice:
        '投资者关系及法律页面仅以英文发布。公告为在 SGXNet 上发布的正式记录。',
      readInEnglish: '阅读英文版',
      autoLabel: '已匹配语言',
      autoNotice:
        '我们已按您浏览器的语言设置显示本页。本页为译文草稿，尚待集团核准——若两者有差异，概以英文版本为准。',
      skipToContent: '跳至主要内容',
      scrollToTop: '返回顶部',
      viewPhoto: '查看照片',
      downloadPhoto: '下载照片',
      previousPhoto: '上一张照片',
      nextPhoto: '下一张照片',
      close: '关闭',
      chapters: '章节',
      playFrom: '从此处播放',
      videoSilent: '无声录制 · 无旁白',
      videoUnsupported: '您的浏览器无法播放此视频。',
      captionsMalay: '马来文字幕',
      downloadVideo: '下载视频（MP4）',
      menu: '菜单',
      menuNote: '网站导航、搜索与语言选项。',
      cookieSettings: 'Cookie 设置',
      awaitingApproval: '待核准',
      poweredBy: '技术支持',
      personnelProfile: '人员简介',
      printDocument: '打印或另存为 PDF',
      printHint: '打印件会载明版本、生效日期与来源网址。',
      skipIntro: '跳过',
      listedOn: '上市于',
      rightsReserved: '版权所有。',
    },
    cookies: {
      aria: 'Cookie 偏好设置',
      copy: '我们使用 Cookie 来运营本网站并改善您的浏览体验。您可以接受全部 Cookie，或自行设置偏好。',
      policy: 'Cookie 政策',
      necessaryLabel: '必要 Cookie',
      necessaryNote: '网站运行所必需，始终启用。',
      necessaryAria: '必要 Cookie',
      analyticsLabel: '分析 Cookie',
      analyticsNote: '帮助我们了解网站的使用情况。',
      marketingLabel: '营销 Cookie',
      marketingNote: '用于评估我们推广活动的成效。',
      manage: '设置偏好',
      save: '保存偏好',
      reject: '拒绝非必要 Cookie',
      accept: '接受全部',
    },
    trading: {
      aria: '交易状态',
      status: '交易状态',
      resumption: '复牌',
      link: '股份资料 →',
      dismiss: '关闭交易状态提示',
    },
    home: {
      scroll: '向下滑动',
      glanceAria: '集团概况',
    },
    group: {
      eyebrow: '集团',
      h1: '集团',
    },
    structure: {
      heading: '实体与持股',
      listedHolding: '上市控股公司',
      tapHint: '点选任一实体查看其简介',
      openProfile: '打开实体简介：',
      profileEyebrow: '实体简介',
      activity: '业务活动',
      ownership: '持股',
      heldBy: '持有方',
      directStake: '直接持股',
      position: '在集团中的位置',
      subsidiaries: '直接子公司',
      noSubsidiaries: '在集团架构中并无子公司。',
      noteLabel: '附注',
      close: '关闭',
      segmentsLabel: '业务分部：',
      crossHolding: '由 {holder} 持有 {stake}',
      chartEyebrow: '集团架构',
      chartTitle: '及其子公司',
      scrollHint: '横向滑动图表以查看全部实体',
      zoomHint: '双指缩放、双击或使用控制按钮放大图表',
      zoomIn: '放大',
      zoomOut: '缩小',
      zoomFit: '适应屏幕',
      type: {
        listed: '上市公司',
        holding: '控股公司',
        operating: '运营公司',
        dormant: '暂无营运／物业持有',
      },
      segments: {
        marine: '海事',
        instrumentation: '仪器',
        healthcare: '医疗保健',
      },
    },
    fishingApp: {
      layersAria: '数据图层',
      illustrative: '示意图 · 非实时数据',
      play: '播放七天预报',
      pause: '暂停七天播放',
      timeline: '七天时间轴',
      days: ['周三', '周四', '周五', '周六', '周日', '周一', '周二'],
      species: ['印度鲭', '鱿鱼', '金枪鱼'],
      layers: [
        { label: '水温', detail: '海面温度梯度。' },
        { label: '浪高', detail: '经放大的涌浪起伏。' },
        { label: '叶绿素', caption: '近岸藻华带', detail: '鱼群觅食之处。' },
        { label: '风', caption: '5 节 · 阵风 6 节', detail: '动态风场流线。' },
        { label: '鱼群', caption: '鱼种概率', detail: '印度鲭 · 鱿鱼 · 金枪鱼' },
        { label: '导航', caption: '等深线 · 航迹 · AIS', detail: '航海图模式。' },
      ],
    },
    businesses: {
      eyebrow: '我们的业务',
      h1: '我们的业务',
      brandPartners: '品牌伙伴',
      typeOnlyNote:
        '各品牌标志均为其所有者的财产，按所提供的原样展示。',
      boats: '皮划艇',
      boatsRailAria: 'Supratechnic 分销的皮划艇',
      step: '步骤',
      suprayiClients: 'SupraYi 精选客户',
      suprayiClientsNote:
        '以上为 suprayi.com 所列的 SupraYi 客户。授权将在正式发布前确认；并非作为集团层面的客户推荐。',
      iqairRailAria: 'SupraYi 提供的 IQAir 产品',
      siiPartnersNote: '这四家伙伴并无品牌标志文件，因此以文字墙呈现，不做任何仿制。',
      instrumentation: '仪器与安全产品',
      instrumentationRailAria: 'SII 供应的仪器与气体检测产品',
      certifications: '认证',
      siiLeadership: 'SII 管理团队',
      fishingAppEyebrow: '应用智能 · 智能捕鱼应用',
      fishingAppTitle: '海事与渔业智能',
      fishingAppCopy:
        '卫星、海洋与船舶数据转化为船长在离港之前即可采取的决策——三种语言、可离线使用、并掌握整个船队动态。',
      comingSoon: '即将推出',
      registryCaption: '权益登记册的示意呈现。',
    },
    governance: {
      eyebrow: '公司治理',
      h1: '公司治理',
      managementTeam: '管理团队',
      composition: '成员组成',
      chair: '主席',
      member: '成员',
    },
    sustainability: {
      eyebrow: '可持续发展',
      h1: '可持续发展',
    },
    contact: {
      eyebrow: '联络我们',
      name: '姓名',
      email: '电子邮箱',
      phone: '电话',
      type: '查询类别',
      subject: '主题',
      message: '内容',
      select: '请选择…',
      types: {
        General: '一般查询',
        Investor: '投资者',
        Media: '媒体',
        Partnership: '合作',
      },
      send: '发送信息',
      privacyBefore: '提交即表示您同意我们的',
      privacyLink: '隐私政策',
      privacyAfter: '。您的资料仅用于回复此次查询。',
      telephone: '电话',
      emailLabel: '电子邮箱',
      websiteLabel: '网站',
      hoursLabel: '办公时间：',
      irHeading: '投资者关系',
      irNote: '有关集团及其信息披露的股东、分析师与媒体咨询。',
      irLink: '投资者关系联系方式 →',
      errors: {
        name: '请填写您的姓名。',
        email: '请填写有效的电子邮箱地址。',
        type: '请选择查询类别。',
        message: '请填写查询内容。',
      },
      toastCheck: '请检查标示的栏位',
      toastReceived: '已收到您的信息',
      toastError: '出现问题',
    },
  },

  HOME: {
    hero: {
      eyebrow: '新交所主板 · 新加坡 · 马来西亚 · 印度尼西亚',
      h1: '深耕分销的传统，以智能构建的未来。',
      sub:
        '本集团业务横跨多国，覆盖海事、工业、医疗保健与科学仪器领域，如今正着力建设将定义下一篇章的人工智能、自动化与营销科技能力。',
      primary: { label: '投资者关系 →' },
      secondary: { label: '了解集团' },
    },
    latest: {
      eyebrow: '集团最新动态',
      heading: '监管公告',
      viewAll: { label: '查看全部公告 →' },
      empty: '集团恢复在 SGXNet 上发布文件后，公告将在此显示。',
    },
    stats: [
      { label: '年区域分销经验' },
      { label: '个国家——新加坡、马来西亚、印度尼西亚' },
      { label: '个代理品牌系列' },
      { label: '主板上市' },
    ],
    ecosystem: {
      eyebrow: '生态体系',
      h2: '一个集团。三大支柱。一个飞轮。',
      intro:
        '我们的业务彼此支撑：一部产生真实运营数据的营收引擎，一层把数据转化为工具的智能中枢，以及一个让两者不断复利的能力加速器。',
      cards: [
        {
          label: '分销与仪器',
          title: '营收引擎',
          copy:
            `Supratechnic 与 SII——逾 ${HERITAGE_YEARS} 年在海事、工业、医疗保健与科学仪器领域值得信赖的区域分销经验，是集团现金流的骨干。`,
          link: { label: '了解各项业务 →' },
        },
        {
          label: '人工智能、营销科技与集团服务',
          title: '中枢神经系统',
          copy:
            'SuprAI Pte Ltd 负责建设集团的人工智能、自动化与营销科技工具——先在自身业务上验证，再产品化对外服务客户。由成本中心转为利润中心。',
          link: { label: 'SuprAI 如何运作 →' },
        },
        {
          label: '技术加速与实物资产',
          title: '能力加速器',
          copy:
            '在一家科技初创公司的战略性联营持股为集团的技术与产品能力提速——实物资产代币化则作为经监管机构验证的未来选项。',
          link: { label: '我们的技术路径 →' },
        },
      ],
    },
    fishingApp: {
      eyebrow: '应用智能 · 智能捕鱼应用',
      h2: '把智能带到真正作业的现场。',
      intro:
        '智能捕鱼应用专为集团服务逾半世纪的海域而打造。它把卫星、海洋与船舶数据转化为船长在离港之前即可采取的决策——海面温度、浪高、叶绿素、风况、鱼种预测与导航，三种语言，在手机上，且可离线使用。',
      tiles: [
        {
          title: '三语原生',
          copy: '英文 · 马来文 · 印尼文——为真正使用它的船员而设计。',
        },
        {
          title: '可离线使用',
          copy: '离线地图与省流量模式，因为信号往往止于海岸线。',
        },
        {
          title: '掌握船队动态',
          copy: '船位、航迹与安全图层，尽在同一张图上。',
        },
      ],
      closing:
        '智能捕鱼应用是集团的能力，由 SuprAI Pte Ltd 基于 Supratechnic 与 SII 的运营经验开发。',
    },
    techBand: {
      eyebrow: '技术与集团的交汇处',
      h2: '三个界面，一套能力堆栈。',
      tiles: [
        {
          eyebrow: '应用人工智能',
          copy: '海事与渔业智能，运行在集团早已服务的海域之上。',
          link: { label: '查看海洋场景 →' },
        },
        {
          eyebrow: 'SuprAI 技术堆栈',
          title: '信号进入，结构输出',
          copy: '杂乱的运营数据被梳理成能让每项业务更锐利的工具。',
          link: { label: 'SuprAI 如何运作 →' },
        },
        {
          eyebrow: '实物资产代币化',
          title: '有节制的选项',
          copy: '实物资产代币化作为经监管机构验证的选项持有，将随该市场的成熟稳步探索。',
          link: { label: '我们的技术路径 →' },
        },
      ],
    },
    cta: {
      h2: '以纪律为本。为未来布局。',
      copy:
        '在恢复于新交所主板交易之际，我们承诺保持透明、稳健治理，并以清晰的策略创造价值。',
      button: { label: '投资者关系 →' },
      micro: '股价与财务资料将在交易恢复后更新。',
    },
  },

  FLYWHEEL: {
    eyebrow: '飞轮',
    h2: '集团如何实现复利。',
    closing: '飞轮再次转动，每一圈都更快。',
    groupCopy:
      '分销带来营收与数据。人工智能与营销科技把数据转化为让每项业务更锐利的工具。一家科技初创公司加速建设。资本与人才回流至分销——飞轮再次转动，每一圈都更快。',
    stages: [
      {
        title: '分销',
        copy: '分销在三个国家带来营收与真实运营数据——涵盖海事、工业、医疗保健与科学仪器。',
      },
      {
        title: '人工智能与营销科技',
        copy: '人工智能与营销科技把数据转化为让每个运营实体更锐利的工具——先在内部验证，再对外销售。',
      },
      {
        title: '技术提速',
        copy: '一家科技初创公司为集团的技术与项目能力提速，实物资产代币化则作为并行选项。',
      },
      {
        title: '资本与人才',
        copy: '资本与人才回流至分销——飞轮再次转动，每一圈都更快。',
      },
    ],
  },

  GROUP: {
    overview: {
      h2: '关于集团',
      paragraphs: [
        `本集团总部设于新加坡，在新交所主板上市，业务遍及新加坡、马来西亚与印度尼西亚。逾 ${HERITAGE_YEARS} 年来，我们的分销业务为监管严格、要求严苛的行业客户供应优质的海事、工业、医疗保健与科学仪器。`,
        '今天，集团正在转型。我们正在建设人工智能、自动化与营销科技能力——先在自身运营公司验证，再向区域内外的客户提供。我们的目标是从一家受人尊敬的区域分销商，成长为双引擎集团：既有稳固的营收基础，又有在其之上成长的智能业务。',
      ],
      pullQuote: '一个集团，以纪律执行，汇聚成信任——并转化为价值。',
    },
    pillars: {
      h2: '三大支柱，合而为一',
      intro: '我们的策略立足于同一家上市控股公司下的三大支柱，彼此相互强化。',
      paragraphs: [
        `Supratechnic 与 SII 是集团的营收引擎：逾 ${HERITAGE_YEARS} 年在三个国家从事海事、工业、医疗保健与科学仪器的分销。它们带来经常性收入，以及支撑其上一切的真实运营数据。`,
        'SuprAI Pte Ltd 是集团的中枢神经系统。它建设人工智能、自动化与营销科技产品，先应用于自身业务，并集中管理集团的共享服务。经内部验证后，这套技术堆栈便成为可以对外销售的产品。',
        '通过在一家科技初创公司的战略性少数股权，集团得以加速取得当下所需的技术、产品开发与项目管理能力——实物资产代币化则作为经监管机构验证的未来选项。',
      ],
    },
    milestones: {
      h2: '我们的历程',
      intro: '从区域分销业务，到正在转型的上市集团。',
      items: [
        { year: '1968 年', text: 'Supratechnic Pte Ltd 于新加坡注册成立，奠定集团分销业务的基础。' },
        { year: '1975 年', text: 'SII 成立，为集团引入科学与工业仪器业务。' },
        { year: '2007 年 1 月 31 日', text: '于新加坡交易所主板上市。' },
        { year: '2024 年 2 月 23 日', text: '公司股票停牌。' },
        {
          year: '2024 年 6 月 11 日',
          text: '共同及各别司法管理人获委任。',
          detail: '临时司法管理人已于 2024 年 3 月 11 日获委任。',
        },
        {
          year: '2025 年 9 月 19 日',
          text: '与 Ascendro Group Limited 签订有条件贷款融资协议。',
          detail:
            'Ascendro 承诺提供 300 万新元——其中 200 万新元用于偿债安排下向债权人的分派，100 万新元用于专业费用及重组开支。',
        },
        { year: '2026 年 4 月 9 日', text: '债权人通过偿债安排。' },
        { year: '2026 年 5 月 21 日', text: '高等法院批准偿债安排。' },
        { year: '2026 年 7 月 18 日', text: '公司解除司法管理。' },
        {
          year: '2026 年 7 月 30 日',
          text: '董事会重组，独立董事占多数，并设立人工智能与科技委员会。',
        },
        {
          year: '2026 年 8 月',
          text:
            '获委任为马来西亚半岛 Tohatsu 舷外机授权经销商；SII 于 2026 年 8 月 26 日与 Kimera Health 集团签订合营协议。',
        },
        { year: '2026 年 10 月 26 日', text: 'FY2026 经审计财务报表发布，审计意见为无保留意见。' },
        { year: '2026 年 11 月 12 日', text: '召开涵盖 FY2022 至 FY2026 的合并股东周年大会。' },
        {
          year: '待定',
          pending: true,
          text: '恢复在新交所主板交易。',
          detail: '须经新交所证券交易有限公司批准，并无法保证。',
        },
      ],
    },
    structure: {
      h2: '我们的组织架构',
      intro: '集团是上市控股公司，其下为构成三大支柱的运营与资本实体。',
      notesLabel: '附注',
      notes: [
        'SuprAI Pte. Ltd. 原名为 Supratechnic SII Pte. Ltd.；SuprAI Capital Pte. Ltd. 原名为 Supratechnic Capital Pte. Ltd.。',
        'Scientific & Industrial Instrumentation Pte. Ltd. 由 SuprAI Pte. Ltd. 持有 100%。',
        'PT Supratechnic Instrumentasi 由 Supratechnic Pte. Ltd. 持有 90%，由 Supratechnic (M) Sdn. Bhd. 持有 10%。',
        '暂无营运、清盘中或正在除名的子公司，以及拟议投资，均未列示。所有子公司的详情载于 FY2026 经审计财务报表。',
      ],
    },
    leadership: {
      h2: '管理层',
      intro: '《2026 年年报》所载的高级管理层与联合公司秘书。',
      source: '依据公司《2026 年年报》（2026 年 10 月 28 日）。',
      pending: '管理层简介将在董事会与公司秘书核准后发布。',
    },
  },

  STRUCTURE_TREE: {
    role: '新加坡 \u00b7 投资控股',
    detail: '于新交所主板上市',
    children: [
      {
        role: '新加坡 \u00b7 中间控股公司',
        children: [
          {
            role: '新加坡 \u00b7 分销控股公司',
            children: [
              { role: '新加坡 \u00b7 海事分销；IQAir 分销' },
              {
                role: '马来西亚 \u00b7 舷外机分销与服务',
                children: [{ role: '马来西亚 \u00b7 物业持有' }],
              },
              { role: '印度尼西亚 \u00b7 实验室仪器与 IQAir 分销' },
            ],
          },
          { role: '新加坡 \u00b7 实验室、校准、验证与气体检测' },
          { role: '新加坡 \u00b7 投资控股' },
        ],
      },
    ],
  },

  BUSINESSES: {
    sub:
      '三大支柱各自专注、各有所长，却在同一个生态体系中彼此供养。三者共同产生构成我们竞争优势的数据、智能与能力。',
    pillar1: {
      eyebrow: '支柱一',
      h2: '营收引擎',
      intro:
        `Supratechnic 与 SII 共同构成集团现金流的骨干——一个历经逾 ${HERITAGE_YEARS} 年建成、跨国跨行业的分销平台。`,
    },
    supratechnic: {
      tagline: '海事 · 工业 · 医疗保健分销',
      copy:
        'Supratechnic 在新加坡、马来西亚与印度尼西亚分销优质船用发动机、工业设备以及医疗保健与空气质量解决方案。与领先品牌的长期合作，支撑起建立在技术公信力与高服务标准之上的声誉。该业务正拓展至电子商务——从 SupraYi 起步——以直接触达客户。',
      facts: ['1968 年注册成立', '新加坡 · 马来西亚 · 印度尼西亚', '2009 年新加坡中小企业 500 强第 249 位', 'Afli 独家分销商 — 新马印'],
      afli: {
        label: '2026 年 10 月',
        h4: 'Afli——面向小型渔民的 AI 捕鱼预测',
        copy:
          'Supratechnic 已与冰岛渔业科技公司 GreenFish hf. 签署《合作协议》，成为 Afli 在马来西亚、新加坡与印度尼西亚的独家分销商；SuprAI Capital 与 GreenFish 共同主导集团在 Afli 上的方向。Afli 结合人工智能、卫星观测与海洋学数据，标示鱼群更可能出现的海域，至少每六小时更新一次，并可在海上离线使用。计划中的航行安全功能包括向岸上联系人发出逾时未归提醒及紧急求救功能，并由每位渔民自行决定是否分享位置。首先将在马来西亚半岛东西两岸至少三家分行，为 50 位渔民提供为期四至六个月、横跨季候风期的免费试行，之后再在七家马来西亚分行商业推出。',
        note: 'Afli 不具备卫星连接，亦不可替代法定安全设备。',
        linkLabel: '签署仪式实况 →',
        demo: {
          label: '观看应用实际操作 →',
          note: 'GreenFish 操作演示 · 9 分 29 秒 · 马来文字幕',
          alt: 'Afli 操作演示截图：应用显示马来西亚半岛与苏门答腊一带海域的风力、浪高与水温状况。',
        },
      },
      heritage:
        '1968 年，Supratechnic Pte Ltd 在新加坡注册成立。2009 年，Supratechnic 在新加坡中小企业（SME）500 强榜单中位列第 249 位。',
      aftersales: {
        eyebrow: '销售 · 维修 · 零配件',
        h4: '不止供货，更重售后',
        copy:
          '分销业务的成败，取决于开票之后。Supratechnic 为其代理的品牌提供销售、维修与零配件——由自有车间与上门维修团队支持。',
        items: [
          { title: '销售', copy: '来自品牌伙伴的船用发动机与设备，覆盖新加坡、马来西亚与印度尼西亚。' },
          { title: '维修', copy: '受训技师在车间或码头作业，让发动机在整个使用年限内保持运转。' },
          { title: '零配件', copy: '为我们供应的发动机与设备提供原厂零配件，使维修不至于变成大修。' },
        ],
        caption: '自 1968 年为海事提供动力——车间与上门维修团队。',
      },
      outboards: {
        h4: '舷外机',
        intro:
          'Supratechnic 在区域内代理 HIDEA 舷外机系列；2026 年 8 月，Supratechnic (M) Sdn. Bhd. 获委任为 Tohatsu 舷外机在马来西亚半岛的授权经销商。',
        footnote:
          '系列与型号名称均为制造商所有。两个系列的产品图片尚待品牌方授权，因此我们不展示任何未获授权的图像。',
      },
      warranty: {
        eyebrow: '拥抱技术',
        h4: '亚洲首个船用舷外机全数字化在线保修系统',
        copy:
          '一个平台，把经销商、供应商与客户无缝连接。客户扫描二维码登记产品；分行完成数字化交车前检查；总部核实；供应商在同一系统中批准。保修索赔同样如此，从故障上报到维修结案，全程在线。',
        builtBy:
          '由 SuprAI 与 Frac 基于 Microsoft Power Platform 打造，并同时以英文、马来文与中文撰写，供一线人员使用。平台不绑定特定产品或供应商：集团分销的任何船用产品、任何供应商均可接入。以下画面来自我们与 HIDEA 的首个部署。',
        roles: [
          { label: '客户', copy: '扫描产品上的二维码完成登记，并上报故障。' },
          { label: '分行', copy: '完成数字化交车前检查，并提出索赔。' },
          { label: '总部', copy: '核实记录，并为供应商整理审核资料。' },
          {
            label: '供应商',
            copy: '批准、驳回、要求补充资料，或因等待零件而暂缓——且只能看到自己的待办队列，别无其他。',
          },
        ],
        facts: [
          '一个平台，三方协同',
          '任何船用产品、任何供应商',
          '二维码登记',
          '数字化交车前检查，无纸化',
          '按角色划分权限',
          '英文 · 马来文 · 中文',
        ],
        link: { label: '阅读操作手册 →' },
        note:
          '以上画面来自平台与 HIDEA 的首个部署，取自其操作手册本身。员工与客户身份信息均已遮蔽。',
      },
    },
    suprayi: {
      tagline: '清净空气 · IQAir · 直达客户',
      copy:
        'SupraYi 是集团直达客户的清净空气业务，由 Supratechnic (S) Pte Ltd 作为 IQAir 在新加坡的授权分销商运营——这家瑞士空气质量品牌，在空气洁净不容妥协的场所深受医院与政府机构信赖。它同时是集团在电子商务、营销科技与客户数据能力上的试验场。',
    },
    sii: {
      tagline: '实验室 · 校准 · 气体检测',
      copy:
        'SII 为监管严格的行业提供精密仪器、校准与气体检测解决方案。其获认证的实验室与校准服务，稳固了集团在企业与机构客户中的公信力，也为未来由人工智能驱动的分析业务提供了自然入口。',
      facts: [
        '1975 年成立',
        'SIICAL 自有校准实验室',
        '新加坡 · 马来西亚 · 印度尼西亚 · 香港 · 新西兰',
        '每年校准逾 1,000 台仪器',
        'IGD 授权分销商 — 新加坡',
        'Kimera Health 合资 — 新马印',
      ],
      igd: {
        label: '2026 年 9 月',
        copy:
          'International Gas Detectors——瑞典总部 Safe Monitoring Group 旗下的英国固定式气体检测制造商——已委任 SII 为其在新加坡的授权分销商及渠道伙伴。《分销商与渠道伙伴协议》于 2026 年 9 月 29 日签署，初始期限三十六个月，涵盖 IGD 气体检测产品在新加坡全境的推广、销售、系统设计支持、安装、调试、维修与校准。该协议将 IGD 的英国工程检测技术与我们获认证的 SIICAL 校准实验室结合，使客户可在同一处取得系统、调试与校准服务。',
        linkLabel: '联合新闻稿 →',
      },
      jv: {
        label: '2026 年 8 月',
        copy:
          'SII 已与 Kimera Health 旗下三家公司签订合资与商业合作协议，签署地点为首尔 CPHI Korea 2026，覆盖新加坡、马来西亚与印度尼西亚。该协议将 SII 的仪器、校准、验证与环境监测能力，以及集团的 IQAir 清洁空气系统，与 Kimera Health 的医疗器械许可证及其在医院、手术中心与专科诊所的渠道结合起来。此合资为非法人形式：不设新公司、无资本投入、无对价。另有一份不具法律约束力的意向书，记录双方有意就 SII 收购其中一家或多家公司最多 51% 股权进行磋商。',
        linkLabel: 'SGXNet 公告 →',
      },
      supportingLine: '对验证经理而言，最重要的问题是谁来校准校准仪。就我们而言，由我们自己完成。',
    },
    siiPartners: [
      { line: '有线与无线热验证。', detail: 'E-Val Pro、TrackSense Pro Mini/Micro、传感器阵列。' },
      {
        line: '英国工程设计的工业气体检测。',
        detail: 'MK8 催化燃烧式传感器、VOC 系列、可寻址控制面板。',
      },
      {
        line: '面向饮料、酒店与医疗保健的二氧化碳安全方案。',
        detail: 'Mk9、Mk10、Scout Glow、SMB Safety Valve。',
      },
      { line: '红外气体传感技术。', detail: '高稳定性红外传感模块。' },
    ],
    pillar2: {
      eyebrow: '支柱二',
      h2: '中枢神经系统',
      intro:
        'SuprAI Pte Ltd 建设集团的人工智能、自动化与营销科技产品，并运营把集团凝聚在一起的共享服务。',
      blocks: [
        {
          title: '验证',
          copy:
            '我们自己的业务是 SuprAI 的首批客户。每一个工具都部署在真实运营之中，而这些业务产生的洞察又反哺出更强的产品。',
        },
        {
          title: '产品化',
          copy:
            '当一个工具切实为我们自身业务带来流量、客流与营收时，这份已获验证的成效——真实客户、真实数据——便使它成为可信、可售的产品。',
        },
        {
          title: '拓展',
          copy:
            '我们先从核心优势市场出发——新加坡与马来西亚——再到印度尼西亚与更广阔的区域，然后走向海外。今天是集团的成本中心，明天将是利润中心。',
        },
        {
          title: '集团服务',
          copy:
            'SuprAI Pte Ltd 亦集中管理集团的财务、人力资源、信息技术、法务与商业治理——减少重复开销，并在三个国家确立统一的运营标准。',
        },
      ],
      builtAndRunning: '已上线运行',
    },
    pillar3: {
      eyebrow: '支柱三',
      h2: '能力加速器',
      intro:
        '通过拟议取得一家科技初创公司的战略性少数股权，集团在人工智能雄心所需的技术与纪律上取得先行优势。',
      blocks: [
        {
          title: '加速起步',
          copy:
            '该初创公司带来集团目前尚未具备的项目管理、产品开发与核心技术能力——让我们得以即刻动工，而非从零开始。',
        },
        {
          title: '保留选项',
          copy:
            '其在马来西亚证券委员会认可市场运营者框架下获得的认证，为集团提供了一个经监管机构认可的实物资产代币化切入点——将随该市场的成熟稳步探索。',
        },
        {
          title: '推进次序',
          copy:
            '人工智能、自动化与营销科技先行——这是每一家中小企业的当务之急。实物资产代币化并行推进，日后再扩大规模。',
        },
      ],
      disclaimer:
        '对该科技初创公司的投资仍属拟议，尚待取得相关批准。本页任何内容均不构成要约或要约邀请。',
    },
  },

  GOVERNANCE: {
    sub:
      '稳健的治理，是我们与各方重建信任的基石。集团承诺遵循《新加坡公司治理准则》的原则，并坚持透明、负责的领导。',
    board: {
      h2: '董事会',
      intro: '我们的董事会汇聚了资本市场、行业、财务与治理方面的经验。',
      pending: '董事简介将在董事会与公司秘书核准后发布。',
    },
    exco: {
      h2: '执行委员会',
      intro: '集团执行委员会负责领导三个国家、三大支柱的日常管理。',
      pending: '董事简介将在董事会与公司秘书核准后发布。',
    },
    committees: {
      h2: '董事会下设委员会',
      intro: '董事会由四个下设委员会提供支持，各委员会均有其自身的组成与职责范围。',
      items: [
        { note: '于 2026 年 7 月 30 日重组。' },
        { note: '于 2026 年 7 月 30 日重组。' },
        { note: '于 2026 年 7 月 30 日重组。' },
        { note: '于 2026 年 7 月 30 日设立，职权范围于 2026 年 10 月 16 日通过。' },
      ],
      source: '依据公司《2026 年年报》（2026 年 10 月 28 日）。',
    },
    policies: {
      h2: '公司治理与政策',
      intro: '我们的治理框架与主要政策载于下方。',
      items: [
        {
          title: '公司治理报告',
          caption: '《2026 年年报》· 第 15–79 页',
          linkLabel: '在年报中阅读 →',
        },
        {
          title: '董事商业行为与道德准则',
          caption: '于 2026 年 10 月 16 日通过 · 文件待发布',
        },
        {
          title: '举报政策',
          caption: '于 2026 年 10 月 16 日通过 · 文件待发布',
          note:
            '集团员工及外部人士可向审计与风险管理委员会主席反映关切事项。举报人身份始终保密。',
        },
        {
          title: '股息政策',
          statement:
            '公司目前没有正式的股息政策，并拟在 FY2027 年报中制定并披露该政策。董事会未就 FY2026 建议派发股息。',
        },
        {
          title: '投资者关系政策',
          statement:
            '公司拟于 FY2027 采纳正式的投资者关系政策，以配合《上市手册》第 710B 条于 2027 年 1 月 1 日生效。',
        },
      ],
      source: '依据公司《2026 年年报》（2026 年 10 月 28 日）。',
    },
  },

  SUSTAINABILITY: {
    intro:
      '作为一家新交所上市集团，我们认识到自身有责任以可持续的方式经营，并就环境、社会与治理表现作出透明的报告。',
    reporting: {
      h2: '可持续发展报告',
      paragraphs: [
        '新交所监管公司已就 FY2022 至 FY2026 豁免本公司的可持续发展报告要求，相关细节已于 2026 年 10 月 7 日公布。',
        '在此期间，公司长期处于司法管理之下，股票自 2024 年 2 月 23 日起停牌，集团业务大幅收缩。',
        '在完成合规化程序后，公司拟自 FY2027 起遵守新交所证券交易有限公司《上市手册》项下适用的可持续发展报告要求。',
      ],
    },
    foundations: {
      h2: '治理基础',
      items: [
        '董事会于 2026 年 7 月重组，独立董事占多数。',
        '审计与风险管理委员会、提名委员会及薪酬委员会均由独立董事担任主席。',
        '人工智能与科技委员会负责监督集团对科技与数据的运用，以及相关的网络安全、数据保护与治理风险。',
        '董事商业行为与道德准则，以及经更新的举报政策，已于 2026 年 10 月 16 日通过。',
        '独立审计师就 FY2026 财务报表出具无保留意见。',
      ],
    },
    people: {
      h2: '我们的员工',
      text: '集团在马来西亚、新加坡与印度尼西亚的业务已推行全新的《员工手册》及奖惩制度。',
    },
    archive: {
      h2: '报告存档',
      text: '公司在新交所《上市手册》项下的首份可持续发展报告预计于 FY2027 发布。',
    },
    source: '依据公司《2026 年年报》（2026 年 10 月 28 日）。',
  },

  VERIFIED: {
    eyebrow: '投资者关系',
    h1: '已验证文件',
    intro:
      '我们的主要披露文件，每一份都已封存于公共区块链，任何人都可以确认手中的副本与我们发布的完全一致。',
    notice:
      'SGXNet 是公司公告的权威记录。本页是一项额外的核对：它证明下列文件就是在所示日期发布于本网站的文件，且此后未被更改。',
    steps: [
      { title: '于 SGXNet 发布', text: '每份文件均先通过 SGXNet 向市场发布。' },
      {
        title: '封存于区块链',
        text:
          '我们将每份文件的数字指纹记录在公共区块链上——一份公开记录，其日期任何人（包括公司本身）都无法更改。',
      },
      {
        title: '自行核对任何副本',
        text: '在下方上传一份 PDF；您的设备会计算其指纹，并告知是否相符。',
      },
    ],
    documents: {
      h2: '已封存文件',
      releasedLabel: '于 SGXNet 发布',
      viewLabel: '查看文件',
      technicalLabel: '技术细节',
      fingerprintLabel: 'SHA-256 指纹',
      sizeLabel: '文件大小',
      proofLabel: '下载区块链凭证（.ots）',
      verifyLabel: '在 OpenTimestamps 验证',
      independent:
        '如需独立验证，请下载该文件及其凭证文件，打开 opentimestamps.org 并将两个文件拖放到页面上。',
      bases:
        '年报说明，该等无法表示意见的依据主要涉及司法管理之前及期间的记录、函证与事项（《2026 年年报》第 3 页）。',
      pending: '于 {sealed} 封存。区块链确认进行中（通常需要数小时）。',
      confirmed: '于 {date} 封存。已于 {blockDate} 在公共区块链上确认（区块 {height}）。',
      confirmedNoTime: '于 {date} 封存。已在公共区块链上确认（区块 {height}）。',
      empty: '首个凭证记录完成后，已封存文件将在此显示。',
      bySlug: {
        'annual-report-2026': {
          title: '《2026 年年报》（FY2022\u2013FY2026）',
          opinion: '载有 FY2026 经审计财务报表；审计师就 FY2026 出具无保留意见。',
        },
        'fs-fy2025': {
          title: '经审计财务报表，截至 2025 年 3 月 31 日止财政年度',
          opinion:
            'PKF-CAP LLP：就集团合并财务表现及现金流量无法表示意见；就集团与公司的财务状况出具无保留意见。',
        },
        'fs-fy2024': {
          title: '经审计财务报表，截至 2024 年 3 月 31 日止财政年度',
          opinion: 'PKF-CAP LLP：无法表示意见。',
        },
        'fs-fy2023': {
          title: '经审计财务报表，截至 2023 年 3 月 31 日止财政年度',
          opinion: 'PKF-CAP LLP：无法表示意见。',
        },
        'fs-fy2022': {
          title: '经审计财务报表，截至 2022 年 3 月 31 日止财政年度',
          opinion: 'PKF-CAP LLP：无法表示意见。',
        },
      },
    },
    checker: {
      h2: '核对他人发给您的副本',
      label: '选择 PDF 文件',
      hint: '不会上传任何内容。您的浏览器在您自己的设备上读取文件并计算其指纹。',
      working: '正在计算指纹……',
      yourFingerprint: '您文件的指纹',
      match: '此文件与 {date} 发布的 {title} 完全一致。',
      noMatch:
        '此文件与本页任何文件均不相符。它可能已被更改，或属于不同版本。请查阅 SGXNet 获取权威副本。',
      error: '无法读取该文件。请选择 PDF 文件。',
    },
    faq: {
      h2: '常见问题',
      items: [
        {
          q: '什么是区块链？',
          a: '一份由全球数千台计算机共同保存的公开记录。一旦写入，便无法更改或倒填日期。',
        },
        {
          q: '这个封存证明了什么？',
          a:
            '证明这些文件最迟在所示日期已发布于本网站，且此后未被更改。除此之外，它并不证明文件内容的任何事项；审计意见已载于每张卡片上。',
        },
        {
          q: '这是否取代 SGXNet？',
          a: '不会。SGXNet 仍是公司所有公告的权威来源。',
        },
        {
          q: '为何要为已在 SGXNet 发布的文件封存？',
          a:
            '文件常以附件形式转发。此核对让任何人无需信任发送者，即可确认转发的副本未被更改。',
        },
      ],
    },
  },

  NEWSROOM: {
    eyebrow: '新闻中心',
    h1: '新闻中心',
    sub: '来自集团各业务的新闻、媒体报道与最新动态。',
    press: {
      h2: '新闻稿',
      intro: '我们最新的新闻与企业动态，均按照监管披露义务发布。',
      categoryLabel: '类别',
      categories: [{ label: '全部' }, { label: '企业' }, { label: '业务' }, { label: '科技' }],
      fromLabel: '起始日期',
      toLabel: '结束日期',
      searchLabel: '搜索',
      searchPlaceholder: '搜索新闻稿',
      readLabel: '阅读 →',
      readAria: '阅读该新闻稿',
      emptyLabel: '暂无新闻稿',
      emptyCopy: '新闻稿发布后将显示于此。所有新闻稿均按照集团的监管披露义务发布。',
      englishNote: '新闻稿标题与摘要保留英文：这是在 SGXNet 上发布的原文。',
    },
    coverage: {
      h2: '媒体报道',
      intro: '媒体对集团的精选报道。',
      emptyLabel: '暂无报道',
      emptyCopy: '精选媒体报道发布后将列于此处。',
      englishNote: '标题按媒体原文语言显示。',
    },
    filings: {
      h2: '最新 SGXNet 公告',
      intro: '公司在 SGXNet 上发布的最近三则公告。此为正式记录：标题与文件均按呈报原文呈现。',
      viewAll: { label: '查看全部公告 →' },
      empty: '待集团恢复在 SGXNet 呈报后，公告将显示于此。',
    },
    gallery: {
      h2: '媒体图库',
      intro:
        '集团工作实景的照片——我们的团队、实验室与安装现场。每张图片均记录其发布来源及所呈现的内容，可按需提供作编辑用途。',
      featureEyebrow: '自 1975 年',
      sourceLabel: '来源',
      requestLabel: '索取原始文件 →',
    },
    leadership: {
      h2: '首席执行官在市场中的声音',
      intro:
        '首席执行官 Melvin Tan Wei Yang 就集团的商业进展及新加坡资本市场现状发表文章，每篇均附全文链接。',
      linkLabel: '在 LinkedIn 阅读全文 →',
      noImageLabel: '文字帖文',
      quoteNote: '引文保留其发布时的原文语言。',
    },
    enquiries: {
      h2: '媒体查询',
      intro: '媒体查询请联系我们的传讯团队。',
      contactLabel: '传讯联络人',
      linkLabel: '联系传讯团队 →',
    },
    assets: {
      h2: '图片与品牌资料',
      intro: '供媒体使用的核准标志与图像可按需提供。',
      link: { label: '索取资料 →' },
    },
  },

  MEDIA_GALLERY: [
    {
      title: 'Afli——与 GreenFish 的合作在吉隆坡签署',
      alt:
        '两位签署人坐于签约桌前，五位同事站立其后，背景写着“Supratechnic × GreenFish — Collaboration Agreement Signing Ceremony”。',
      context:
        'Supratechnic 于 2026 年 10 月 14 日与冰岛渔业科技公司 GreenFish hf. 签署《合作协议》，成为面向小型渔民的 AI 捕鱼应用 Afli 在马来西亚、新加坡与印度尼西亚的独家分销商。为期横跨季候风期的 50 位渔民免费试行将先行展开，之后再在七家马来西亚分行商业推出。签署仪式的十四张照片，以及 GreenFish 的应用操作演示，随新闻稿一并发布。',
      caption: 'Mercure Kuala Lumpur Trion，2026 年 10 月 14 日。',
      sourceLabel: '新闻稿与完整图集',
    },
    {
      title: 'IGD 委任 SII 为其新加坡授权分销商',
      alt:
        'Melvin Tan Wei Yang 先生与 Uu Su Haw 先生在会议室桌前签署《分销商与渠道伙伴协议》，四位 USP Group 董事与高管站在其后。',
      context:
        'International Gas Detectors 已委任 SII 为其在新加坡的授权分销商及渠道伙伴，并于 2026 年 9 月 29 日签署《分销商与渠道伙伴协议》，初始期限三十六个月。协议涵盖 IGD 英国工程固定式气体检测系统在新加坡全境的推广、销售、安装、维修与校准支持，并结合 SII 的 SIICAL 校准实验室与验证服务。',
      caption:
        '从左至右：USP Group Limited 独立非执行董事 Toh Hai Joo 先生；USP Group Limited 独立非执行主席 Chan Chung Khang 先生；Scientific & Industrial Instrumentation Pte. Ltd. 首席执行官、USP Group Limited 执行董事兼首席执行官 Melvin Tan Wei Yang 先生；Safe Monitoring Group 亚太区总裁 Uu Su Haw 先生；USP Group Limited 首席财务官 Leow Yong Kin 先生；以及 USP Group Limited 非独立非执行副主席 Maio Dangi 女士，摄于 2026 年 9 月 29 日《分销商与渠道伙伴协议》签署仪式。',
      sourceLabel: '联合新闻稿',
    },
    {
      title: '交换已签署的协议',
      alt:
        'Melvin Tan Wei Yang 先生与 Uu Su Haw 先生交换已签署的《分销商与渠道伙伴协议》文件夹，USP Group 董事与高管在旁见证。',
      context:
        'IGD 是瑞典总部 Safe Monitoring Group 旗下的英国固定式气体检测制造商，拥有逾 100 年气体检测工程积累。此项委任是该集团在 2026 年初进入亚太后，区域增长战略的又一步，也让新加坡客户可在同一处获得从系统设计、安装到校准、维修与全生命周期支持的完整服务。',
      caption:
        'SII 首席执行官 Melvin Tan Wei Yang 先生与 Safe Monitoring Group 亚太区总裁 Uu Su Haw 先生交换已签署的协议。',
      sourceLabel: '联合新闻稿',
    },
    {
      title: 'Scientific & Industrial Instrumentation 五十年',
      alt:
        'Scientific & Industrial Instrumentation 的员工、家属与嘉宾在新加坡约克酒店的吊灯下合影，身后屏幕写着“Celebrating 50 years of Science”。',
      context:
        'Scientific & Industrial Instrumentation Pte. Ltd.（SII）于 1975 年在新加坡成立——那正是新加坡开始迈向东南亚制造与生命科学枢纽的年代。自成立之初，我们的宗旨便是为区域内要求最严苛的行业提供符合国际标准所需的测量、校准与气体安全能力。',
      caption: 'SII 五十周年庆典，新加坡。',
    },
    {
      title: 'Kimera Health 合资协议在首尔签署',
      alt: 'SII 与 Kimera Health 代表团在合资及商业合作协议签署当日于首尔一家酒店大堂合影。',
      context:
        'SII 与 Kimera Health 旗下三家公司于 2026 年 8 月 26 日在首尔 CPHI Korea 2026 签署合资及商业合作协议，涵盖新加坡、马来西亚与印度尼西亚，内容包括联合营销、分销、注册与售后服务——并涵盖集团在新加坡及印度尼西亚的 IQAir 洁净空气产品。该合资为非法人形式：不设立新公司、无资本投入、无对价。',
      caption: '双方代表团于首尔，2026 年 8 月 26 日。',
      sourceLabel: 'SGXNet 公告',
    },
    {
      title: 'Supratechnic 车间与服务中心',
      alt: '三名身穿品牌制服的 Supratechnic 服务工程师站在车间与服务中心外的服务车旁。',
      context:
        '销售是海事业务的门面，服务才是维系业务的根本。车间与服务中心备有零件以及安装零件的工程师，让购买舷外机的客户有地方送修。',
      caption: '服务工程师与移动服务车。',
    },
    {
      title: '校准作业进行中',
      alt: '一名身穿白色实验袍的 SII 工程师在实验台上为校准仪器连接管路。',
      context:
        'SIICAL 是自有实验室，而非外包安排。仪器由 SII 工程师依据集团自行拥有并维护的参考标准进行校准——这正是审计师查问时证书站得住脚的原因。',
      caption: '工程师正在校准客户的仪器。',
    },
    {
      title: '气体探测在安装现场校准',
      alt: '一名身穿反光背心的 SII 技术员正对安装在厂房墙面上的固定式气体探测器施加测试气体。',
      context:
        '固定式气体探测必须在现场验证。团队在现场为每个探头施加测试气体、记录响应并为该安装出具证书——覆盖区域内的制药、半导体及油气设施。',
      caption: '在现场对固定探头进行冲击测试。',
    },
    {
      title: 'CO₂ 安全监测',
      alt: '一名身穿实验袍的工程师在饮料生产设施内检修二氧化碳监测设备。',
      context:
        '二氧化碳一旦泄漏，伤人极快。SII 是 LogiCO₂ 委任的区域合作伙伴，负责在啤酒厂、酒店地下室、厨房、医院与学校安装、调试、校准及维护该系统。',
      caption: 'LogiCO₂ 监测系统应用于饮料与酒店场所。',
    },
    {
      title: '热验证系统',
      alt: '验证系统显示屏在热分布测试过程中显示各温度传感器通道。',
      context:
        'Ellab 与 Kaye 验证系统——Validator 2000、E-Val Pro、TrackSense Pro——供应、调试并维护于区域内的制药、生物科技、医疗器械与食品制造商。',
      caption: '热分布测试进行中。',
    },
    {
      title: 'SIICAL 的参考标准',
      alt: 'SIICAL 校准实验室内的 Kambic KK-105 CH 温湿度试验箱。',
      context:
        'Kambic KK-105 CH 试验箱，连同标准铂电阻温度计、Fluke Super-Thermometer、Humor 20 与 Thunder Scientific 2500 发生器，构成实验室校准所依据的参考层级。',
      caption: 'Kambic KK-105 CH 温湿度试验箱。',
    },
    {
      title: 'IQAir 走进家庭',
      alt: '安装于住宅客厅内的 IQAir Atem X 空气净化器。',
      context:
        'SupraYi 是集团直接面向消费者的洁净空气业务。Supratechnic 持有 IQAir 在新加坡的分销委任，销往医院的同级临床级过滤技术，同样进入家庭。',
      caption: '新加坡家庭中的 IQAir Atem X。',
    },
  ],

  LEADERSHIP_POSTS: [
    {
      title: '在 Digital Investor Relations Masterclass 上发言',
      alt:
        'Digital Investor Relations Masterclass 宣传图，写着“Is the market recognising your company’s full value?”，SGX 为策略伙伴。',
    },
    {
      title: 'STI30 报告研究座谈会上的投资者视角',
      alt: 'Black Sun Asia Pacific 的 STI30 报告与沟通研究文本摊放在 SGX 礼堂的桌面上。',
    },
    {
      title: '“Unlocking Value Through Trust”座谈会嘉宾',
      alt: '讲者宣传图，介绍 Melvin Tan 为 Black Sun Global 与 SIAS 的 STI30 研究发布会座谈嘉宾。',
    },
    {
      title: 'SupraYi 成为 IQAir 新加坡销售渠道',
      alt: '《联合早报》报道 Supratechnic 取得 IQAir 新加坡独家分销权及 SupraYi 电商平台启动的版面。',
    },
    {
      title: '从法庭到董事会：托管制度改革与《公司法》',
      alt: 'Melvin Tan 独自坐在礼堂内，身后屏幕写着“Extraordinary General Meeting of USP Group Limited”。',
    },
    {
      title: '获委任为 Hidea Outboards 独家分销商',
      alt:
        'Supratechnic (M) Sdn. Bhd. 首席执行官 Melvin Tan Wei Yang 与 Hidea Power Machinery Co., Ltd. 创办人兼执行主席 Zheng Huali 握手。',
    },
    {
      title: '获 SIAS 专访：人工智能时代的公司治理',
    },
  ],

  CONTACT: {
    h1: '与我们联系',
    intro: '无论您是客户、合作伙伴、投资者或媒体朋友，我们都乐意听取您的意见。',
    privacy: '提交即表示您同意我们的隐私政策。您的资料仅用于回复此次查询。',
    success: '谢谢——我们已收到您的信息，并会尽快与您联系。',
    error: '出现问题。请检查标示的栏位后再试一次。',
    officesHeading: '我们的办事处',
    registered: {
      label: '注册办事处',
      note: '自 2026 年 7 月 28 日起生效，依据公司在 SGXNet 发布的公告。',
    },
    hours: '星期一至星期五，上午 8.30 至下午 5.00（新加坡时间）。',
    offices: [
      { name: '集团总部——新加坡' },
      {
        name: 'Supratechnic',
        note: '船用舷外机销售、服务与配件——在新加坡及马来西亚半岛设有分行，并拥有经销商网络。',
        branches: [{ label: '新加坡分行' }, { label: '马来西亚区域办事处' }],
      },
      { name: 'Supratechnic 印度尼西亚' },
      { name: 'SII Asia' },
      { name: 'SupraYi 新加坡' },
    ],
  },

  OUTBOARD_RANGES: [
    {
      note: 'Hangzhou Hidea Power Machinery。由 Supratechnic 在区域内分销。',
      ranges: [
        { name: '四冲程舷外机' },
        { name: '商用级舷外机' },
        { name: '喷水推进舷外机' },
        { name: 'EFI 与二冲程系列', models: '电子燃油喷射与二冲程系列' },
      ],
    },
    {
      note:
        'Supratechnic (M) Sdn. Bhd. 获马来西亚官方分销商 Tai Hua Agencies (K.L) Sdn. Bhd. 委任为 Tohatsu 舷外机授权经销商，销售区域覆盖马来西亚半岛（SGXNet 公告，2026 年 8 月 18 日）。',
      ranges: [
        { name: '便携式四冲程（2.5–20 马力）' },
        { name: '中功率四冲程（25–140 马力）' },
        { name: '大功率舷外机（150–250 马力）' },
        { name: '二冲程与 EverRun', models: 'M 系列 · MX EverRun 系列 · M25H JET' },
      ],
    },
  ],

  WARRANTY_SCREENS: [
    {
      title: '一个应用，四种角色',
      caption: '主界面。用户能打开哪些功能，取决于其登录时使用的角色——分行、总部、供应商或客户。',
    },
    {
      title: '数字化交车前检查',
      caption: '交车前检查在应用内逐项完成，并可填写备注——无纸化，且未完成检查即不激活保修。',
    },
    {
      title: '供应商在同一系统中作出决定',
      caption:
        '供应商——本次部署中为 HIDEA——只看到属于自己的终审队列：案件编号、类型、分行与状态。可批准、驳回、要求补充资料，或因等待零件而暂缓。',
    },
  ],

  SII_LEADERSHIP: [
    { role: '首席执行官' },
    { role: '首席运营官' },
    { role: '执行董事兼首席财务官' },
    { role: '企业发展主管' },
    { role: '财务与人力资源经理' },
    { role: '市场开发经理' },
    { role: '实验室经理' },
    { role: '助理服务与技术经理' },
    { role: '助理服务与技术经理' },
  ],
};

export default zh;
